import React, { useState, useEffect } from 'react';
import { MessageSquare, TrendingUp, Send, CheckCheck, Eye, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import whatsappAnalyticsApi from '../api/services/whatsappAnalyticsApi';

const WhatsAppAnalytics = ({ brandColors = { primary: '#4f46e5', secondary: '#7c3aed' } }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7days');

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (dateRange === '7days') {
        data = await whatsappAnalyticsApi.getLast7Days();
      } else {
        data = await whatsappAnalyticsApi.getLast30Days();
      }
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load WhatsApp analytics:', error);
      setError(error.message || 'Failed to load analytics');
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: brandColors.primary }}></div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">WhatsApp Analytics</h3>
            </div>
            <p className="text-[11px] text-gray-500 font-medium ml-3.5">Real-time messaging performance</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col items-center justify-center gap-3">
            <div 
              className="animate-spin rounded-full h-8 w-8 border-3 border-t-transparent"
              style={{ borderColor: `${brandColors.primary}20`, borderTopColor: 'transparent' }}
            >
              <div 
                className="h-full w-full rounded-full border-3 border-transparent border-t-current"
                style={{ color: brandColors.primary }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 font-semibold">Loading data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: brandColors.primary }}></div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">WhatsApp Analytics</h3>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-4 border border-orange-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertCircle className="h-4 w-4 text-orange-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-xs font-bold text-gray-900 mb-0.5">Analytics Unavailable</h4>
              <p className="text-[11px] text-gray-600 mb-2">
                {error || 'Unable to connect to service.'}
              </p>
              <button
                onClick={loadAnalytics}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-white hover:bg-gray-50 text-gray-700 text-[10px] font-bold rounded-lg border border-gray-200 transition-all shadow-sm"
              >
                <RefreshCw className="h-3 w-3" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: 'Messages Sent',
      value: analytics?.total_sent || 0,
      icon: Send,
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
      borderClass: 'border-blue-200',
      description: 'Total messages dispatched',
    },
    {
      label: 'Successfully Delivered',
      value: analytics?.delivered || 0,
      icon: CheckCheck,
      gradient: 'from-emerald-500 to-teal-600',
      bgGradient: 'from-emerald-50 to-teal-50',
      borderClass: 'border-emerald-200',
      percentage: analytics?.delivery_rate,
      description: 'Confirmed deliveries',
    },
    {
      label: 'Messages Read',
      value: analytics?.read || 0,
      icon: Eye,
      gradient: 'from-purple-500 to-fuchsia-600',
      bgGradient: 'from-purple-50 to-fuchsia-50',
      borderClass: 'border-purple-200',
      percentage: analytics?.read_rate,
      description: 'Opened by recipients',
    },
    {
      label: 'Delivery Failed',
      value: analytics?.failed || 0,
      icon: XCircle,
      gradient: 'from-rose-500 to-pink-600',
      bgGradient: 'from-rose-50 to-pink-50',
      borderClass: 'border-rose-200',
      percentage: analytics?.failure_rate,
      description: 'Undelivered messages',
    },
  ];

  const totalMessages = analytics?.total_sent || 0;
  const successRate = analytics?.delivery_rate || 0;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: 'var(--primary-color)' }}></div>
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">WhatsApp Analytics</h3>
          </div>
          <p className="text-[11px] text-gray-500 font-medium ml-3.5">Real-time performance insights</p>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Period:</span>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5">
            <button
              onClick={() => setDateRange('7days')}
              className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${ 
                dateRange === '7days'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setDateRange('30days')}
              className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${
                dateRange === '30days'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              30 Days
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`bg-white rounded-xl p-4 shadow-sm border border-gray-100 transition-all duration-300`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2 bg-gray-50 rounded-lg`}>
                <stat.icon className={`h-4 w-4 text-primary`} style={{ color: 'var(--primary-color)' }} />
              </div>
              {stat.percentage !== undefined && (
                <div className="flex items-center gap-0.5">
                  <TrendingUp className="h-2.5 w-2.5" style={{ color: 'var(--primary-color)' }} />
                  <span className="text-[10px] font-bold" style={{ color: 'var(--primary-color)' }}>
                    {stat.percentage?.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
            
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{stat.label}</p>
              <p className="text-xl font-bold text-gray-900">
                {stat.value.toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {totalMessages > 0 && (
        <div 
          className="rounded-xl p-4 border shadow-sm"
          style={{
            background: `linear-gradient(135deg, color-mix(in srgb, var(--primary-color), transparent 95%) 0%, color-mix(in srgb, var(--secondary-color), transparent 95%) 100%)`,
            borderColor: `color-mix(in srgb, var(--primary-color), transparent 80%)`
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
                <MessageSquare className="h-5 w-5" style={{ color: 'var(--primary-color)' }} />
              </div>
              <div>
                <h4 className="text-xs font-bold text-gray-900 mb-0.5">Overall Performance</h4>
                <p className="text-[10px] text-gray-500 font-medium">
                  <span className="font-bold text-xs" style={{ color: 'var(--primary-color)' }}>{successRate.toFixed(1)}%</span>
                  <span> success rate across </span>
                  <span className="font-bold text-gray-900">{totalMessages.toLocaleString()}</span>
                  <span> messages</span>
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Success Rate</p>
                <p className="text-xl font-bold" style={{ color: 'var(--primary-color)' }}>{successRate.toFixed(0)}%</p>
              </div>
              <div className="w-12 h-12">
                <svg className="transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#f3f4f6"
                    strokeWidth="10"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="var(--primary-color)"
                    strokeWidth="10"
                    strokeDasharray={`${successRate * 2.51} 251`}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppAnalytics;
